import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Store,
  Car,
  Wrench,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";

const emptyDealer = {
  name: "",
  phone: "",
  email: "",
  address: "",
  notes: "",
  type: "dealer",
};

export default function Dealers() {
  const [dealers, setDealers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDealer);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const [c, v, o] = await Promise.all([
      api.get("/customers"),
      api.get("/vehicles"),
      api.get("/work-orders"),
    ]);
    setDealers(c.data.filter((x) => x.type === "dealer"));
    setVehicles(v.data);
    setOrders(o.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyDealer);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name,
      phone: d.phone || "",
      email: d.email || "",
      address: d.address || "",
      notes: d.notes || "",
      type: "dealer",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Dealer name is required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, { ...form, type: "dealer" });
        toast.success("Dealer updated");
      } else {
        await api.post("/customers", { ...form, type: "dealer" });
        toast.success("Dealer added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to save dealer");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/customers/${confirmDelete.id}`);
      toast.success("Dealer removed");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Failed to delete dealer");
    }
  };

  const filtered = dealers.filter((d) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const plates = vehicles
      .filter((v) => v.customer_id === d.id)
      .map((v) => (v.license_plate || "").toLowerCase())
      .join(" ");
    return (
      d.name.toLowerCase().includes(q) ||
      (d.phone || "").toLowerCase().includes(q) ||
      (d.email || "").toLowerCase().includes(q) ||
      plates.includes(q)
    );
  });

  const totalVehiclesAcrossDealers = vehicles.filter((v) =>
    dealers.some((d) => d.id === v.customer_id)
  ).length;
  const totalOpenOrdersAcrossDealers = orders.filter(
    (o) =>
      dealers.some((d) => d.id === o.customer_id) &&
      ["new", "in_progress", "ready"].includes(o.status)
  ).length;

  return (
    <div>
      <PageHeader
        testid="dealers-header"
        title="Dealers"
        subtitle="Used-car dealers who trust Car Dream with their inventory."
        actions={
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-muted" />
              <Input
                data-testid="dealer-search-input"
                placeholder="Search dealer, plate, phone..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 w-72 rounded-lg border-2 border-warm bg-white"
              />
            </div>
            <Button
              data-testid="add-dealer-btn"
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Dealer
            </Button>
          </>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card
          className="p-5 rounded-2xl border-warm shadow-card bg-white"
          data-testid="dealer-summary-count"
        >
          <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
            Dealers on File
          </div>
          <div className="font-heading text-3xl font-bold text-[#2B2D42]">
            {loading ? "—" : dealers.length}
          </div>
        </Card>
        <Card
          className="p-5 rounded-2xl border-warm shadow-card bg-white"
          data-testid="dealer-summary-vehicles"
        >
          <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
            Dealer Vehicles
          </div>
          <div className="font-heading text-3xl font-bold text-[#E5A82E]">
            {loading ? "—" : totalVehiclesAcrossDealers}
          </div>
        </Card>
        <Card
          className="p-5 rounded-2xl border-warm shadow-card bg-white"
          data-testid="dealer-summary-open"
        >
          <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
            Active Repairs for Dealers
          </div>
          <div className="font-heading text-3xl font-bold text-[#2B2D42]">
            {loading ? "—" : totalOpenOrdersAcrossDealers}
          </div>
        </Card>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">
          Loading dealers…
        </Card>
      ) : dealers.length === 0 ? (
        <EmptyState
          testid="dealers-empty"
          image="https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="No dealers yet"
          description="Add a used-car dealer to keep track of the vehicles you help them repair."
          action={
            <Button
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="empty-add-dealer"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Dealer
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">
          No dealers match &quot;{query}&quot;.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="dealers-grid">
          {filtered.map((d) => {
            const dealerVehicles = vehicles.filter((v) => v.customer_id === d.id);
            const dealerOrders = orders.filter((o) => o.customer_id === d.id);
            const openCount = dealerOrders.filter((o) =>
              ["new", "in_progress", "ready"].includes(o.status)
            ).length;
            return (
              <Card
                key={d.id}
                className="rounded-2xl border-warm shadow-card overflow-hidden bg-white"
                data-testid={`dealer-card-${d.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 py-5 bg-[#1A1A1A] text-white">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-[#E5A82E]/20 flex items-center justify-center text-[#E5A82E] shrink-0">
                      <Store className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading text-lg font-semibold truncate">
                        {d.name}
                      </div>
                      <div className="text-xs text-white/60 uppercase tracking-widest">
                        Dealer
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(d)}
                      data-testid={`edit-dealer-${d.id}`}
                      className="rounded-lg text-white/80 hover:text-white hover:bg-white/10"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(d)}
                      data-testid={`delete-dealer-${d.id}`}
                      className="rounded-lg text-white/80 hover:text-white hover:bg-white/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-3.5 h-3.5 text-slate-muted shrink-0" />
                      <span className="text-[#2B2D42] truncate">
                        {d.phone || <span className="text-slate-muted italic">no phone</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-3.5 h-3.5 text-slate-muted shrink-0" />
                      <span className="text-[#2B2D42] truncate">
                        {d.email || <span className="text-slate-muted italic">no email</span>}
                      </span>
                    </div>
                  </div>

                  {/* Counts */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-[#FAF9F6] border border-warm p-3">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
                        <Car className="w-3.5 h-3.5" /> Vehicles
                      </div>
                      <div className="font-heading text-2xl font-bold text-[#2B2D42]">
                        {dealerVehicles.length}
                      </div>
                    </div>
                    <div className="rounded-xl bg-[#FAF9F6] border border-warm p-3">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
                        <Wrench className="w-3.5 h-3.5" /> Active Repairs
                      </div>
                      <div className="font-heading text-2xl font-bold text-[#E5A82E]">
                        {openCount}
                      </div>
                    </div>
                  </div>

                  {/* Plates */}
                  {dealerVehicles.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-2">
                        Vehicles on register
                      </div>
                      <div className="flex flex-wrap gap-1.5" data-testid={`dealer-plates-${d.id}`}>
                        {dealerVehicles.map((v) => (
                          <span
                            key={v.id}
                            className="font-mono text-xs font-semibold text-[#1A1A1A] bg-[#F4D35E]/30 border border-[#E5A82E]/40 rounded-md px-2 py-0.5"
                          >
                            {(v.license_plate || "—").toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent orders */}
                  {dealerOrders.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-2">
                        Recent orders
                      </div>
                      <div className="space-y-1">
                        {dealerOrders.slice(0, 3).map((o) => (
                          <Link
                            key={o.id}
                            to={`/work-orders/${o.id}`}
                            data-testid={`dealer-order-${o.order_number}`}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#FAF9F6] transition-colors"
                          >
                            <span className="font-mono text-xs text-slate-muted">
                              {o.order_number}
                            </span>
                            <span className="flex-1 text-sm text-[#2B2D42] truncate">
                              {o.complaint || "No complaint noted"}
                            </span>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-muted" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="dealer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit Dealer" : "Add Dealer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">
                Dealer Name *
              </Label>
              <Input
                data-testid="dealer-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="AutoWorld Motors"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Phone</Label>
                <Input
                  data-testid="dealer-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 555-1234"
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Email</Label>
                <Input
                  data-testid="dealer-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@dealer.com"
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Address</Label>
              <Input
                data-testid="dealer-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Dealer Ave"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Notes</Label>
              <Textarea
                data-testid="dealer-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Preferred contact hours, discount arrangement..."
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-xl border-2 border-warm"
              data-testid="cancel-dealer-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="save-dealer-btn"
            >
              {editing ? "Save Changes" : "Add Dealer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove dealer?</AlertDialogTitle>
            <AlertDialogDescription>
              This also removes every vehicle registered to {confirmDelete?.name} and every repair order linked to them. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-[#E07A5F] hover:bg-[#C86A4F] text-white rounded-xl"
              data-testid="confirm-delete-dealer"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
